/**
 * Website Analytics Tracking Service
 * Tracks pageviews, events, and conversions
 */

import api from './api';

// Generate or retrieve visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('nc_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('nc_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new per browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('nc_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('nc_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Get referrer source
const getReferrerSource = () => {
  const referrer = document.referrer;
  if (!referrer) return 'direct';
  if (referrer.includes('google') || referrer.includes('bing') || referrer.includes('yahoo')) return 'search';
  if (referrer.includes('facebook') || referrer.includes('twitter') || referrer.includes('instagram')) return 'social';
  if (referrer.includes(window.location.hostname)) return 'internal';
  return 'referral';
};

/**
 * Track a page view
 */
export const trackPageView = async (pageName, path) => {
  try {
    await api.post('/analytics/track/pageview', {
      page: pageName || document.title,
      path: path || window.location.pathname,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      screen_width: window.innerWidth,
      session_id: getSessionId(),
      visitor_id: getVisitorId()
    });
  } catch (error) {
    console.debug('Analytics pageview tracking failed:', error);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = async (eventName, eventCategory, eventData = {}) => {
  try {
    await api.post('/analytics/track/event', {
      event_name: eventName,
      event_category: eventCategory,
      event_data: eventData,
      page: window.location.pathname,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      device_type: getDeviceType()
    });
  } catch (error) {
    console.debug('Analytics event tracking failed:', error);
  }
};

/**
 * Track a conversion
 */
export const trackConversion = async (conversionType, serviceType, value = 0) => {
  try {
    await api.post('/analytics/track/conversion', {
      conversion_type: conversionType,
      service_type: serviceType,
      value: value,
      source: getReferrerSource(),
      page: window.location.pathname,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      device_type: getDeviceType()
    });
  } catch (error) {
    console.debug('Analytics conversion tracking failed:', error);
  }
};

// Event categories
export const EventCategories = {
  NAVIGATION: 'navigation',
  BOOKING: 'booking',
  ENGAGEMENT: 'engagement',
  FORM: 'form',
  CTA: 'cta'
};

// Conversion types
export const ConversionTypes = {
  BOOKING_COMPLETED: 'booking_completed',
  BOOKING_STARTED: 'booking_started',
  LEAD_CAPTURED: 'lead_captured',
  PHONE_CALL: 'phone_call',
  WORKER_SIGNUP: 'worker_signup',
  CONTACT_FORM: 'contact_form'
};

// Common events
export const Events = {
  // Navigation
  PAGE_VIEW: 'page_view',
  MENU_CLICK: 'menu_click',
  
  // Booking
  BOOK_SERVICE_CLICK: 'book_service_click',
  SERVICE_SELECTED: 'service_selected',
  BOOKING_FORM_START: 'booking_form_start',
  BOOKING_FORM_STEP: 'booking_form_step',
  BOOKING_FORM_COMPLETE: 'booking_form_complete',
  
  // Engagement
  PHONE_NUMBER_CLICK: 'phone_number_click',
  EMAIL_CLICK: 'email_click',
  SOCIAL_CLICK: 'social_click',
  
  // CTA
  CTA_CLICK: 'cta_click',
  JOIN_TEAM_CLICK: 'join_team_click',
  CONTACT_FORM_SUBMIT: 'contact_form_submit'
};

export default {
  trackPageView,
  trackEvent,
  trackConversion,
  EventCategories,
  ConversionTypes,
  Events
};
